import { Routes } from '@angular/router';
import type { RelatedKey } from './content/page-explore/related-tiles.catalog';
import { RouteBottomCta, RouteHeading } from './route-ui-data';
import type { BreadcrumbItem } from './seo-breadcrumbs';
import { PageSeoData } from './seo-metadata';

export const routes: Routes = [
  // Legacy WordPress URL redirects are handled at the edge via
  // public/_redirects (Cloudflare Workers Static Assets). See
  // e2e/cloudflare-config.spec.ts for HTTP-level validation.

  // About routes — no wrapper needed
  {
    path: 'about',
    children: [
      {
        path: 'bio',
        loadComponent: () =>
          import('./routes/about/bio/bio-content/bio-content.component').then(
            (m) => m.BioContentComponent,
          ),
        // #45: name searches ("russ rodgers" @ pos 3.6) showed 0% CTR with
        // the bare "Bio | Russ Rodgers" title competing against the GBP
        // panel and socials — say who he is in the title itself.
        title: 'Bio: 50+ Years on Bass',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Bio', path: '/about/bio' },
          ] satisfies BreadcrumbItem[],
          // No static jsonLd here (deliberately): the ProfilePage node is
          // set at runtime via SEOService, from inside BioContentComponent's
          // own lazy chunk — see bio-jsonld.ts. Moving this out of the eager
          // route data is what keeps the Person schema (and the module that
          // used to be eagerly imported for it) out of every page's initial
          // bundle (2026-09-14 perf pass).
          seo: {
            description:
              'Meet Russ Rodgers — veteran bassist and educator with over five decades of professional performance and instructional experience.',
          } satisfies PageSeoData,
          heading: {
            title: 'About Russ Rodgers',
            lead: 'Over forty years of teaching and performing bass, with over fifty years of professional playing. Currently teaching live Zoom lessons worldwide from Boynton Beach, Florida.',
          } satisfies RouteHeading,
          related: [
            '/',
            '/about/resume',
            '/about/discography',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to learn from a 40-year bass professional?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'local-credits',
        redirectTo: './credits',
      },
      {
        path: 'national-credits',
        redirectTo: './credits',
      },
      {
        path: 'photos',
        redirectTo: './bio',
      },
      {
        path: 'sounds',
        redirectTo: './bio',
      },
      {
        path: 'my-gear',
        redirectTo: './bio',
      },
      {
        path: 'credits',
        loadComponent: () =>
          import('./routes/about/credits/credits-content/credits-content.component').then(
            (m) => m.CreditsContentComponent,
          ),
        title: 'Local and National Credits',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Credits', path: '/about/credits' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Local and national performance credits for Russ Rodgers, spanning over four decades of professional bass playing.',
          } satisfies PageSeoData,
          heading: {
            title: 'Performance & Recording Credits',
            lead: 'Artists Russ has performed or recorded with, across national tours and the Atlanta + DC scenes.',
          } satisfies RouteHeading,
          related: [
            '/',
            '/about/bio',
            '/about/discography',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to study with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'performance',
        loadComponent: () =>
          import('./routes/about/performance/performance-content/performance-content.component').then(
            (m) => m.PerformanceContentComponent,
          ),
        title: 'Performance',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Performance', path: '/about/performance' },
          ] satisfies BreadcrumbItem[],
          // No static jsonLd here (deliberately): the four VideoObject
          // schemas are set at runtime via SEOService, from inside
          // PerformanceContentComponent's own lazy chunk — see
          // performance-jsonld.ts. Moving this out of the eager route data
          // is what keeps it out of every page's initial bundle (2026-09-14
          // perf pass).
          seo: {
            description:
              'Russ Rodgers — active bass performer based in Boynton Beach, Florida. Recording credits, live performance highlights, and current focus across South Florida.',
          } satisfies PageSeoData,
          heading: {
            title: 'Performance',
            lead: 'Live and recorded bass work across jazz, blues, R&B, soul, swing, Latin, and fusion. Currently performing across South Florida.',
          } satisfies RouteHeading,
          related: [
            '/about/credits',
            '/about/discography',
            '/about/bio',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Interested in booking Russ for a performance?',
            body: 'Russ is currently based in Boynton Beach, FL and active across South Florida. For booking, session work, or clinics, reach out by phone or email.',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'discography',
        loadComponent: () =>
          import('./routes/about/discography/discography-content/discography-content.component').then(
            (m) => m.DiscographyContentComponent,
          ),
        title: 'Discography',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Discography', path: '/about/discography' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Studio recordings featuring Russ Rodgers on bass — blues with Francine Reed and Kodac Harrison, Latin with Henry Parrilla, swing with League Of Decency, and other projects.',
          } satisfies PageSeoData,
          heading: {
            title: 'Discography',
            lead: 'Albums and live recordings Russ has played bass on, across blues, jazz, swing, and Latin projects. Stream tracks inline.',
          } satisfies RouteHeading,
          related: [
            '/about/credits',
            '/about/bio',
            '/about/performance',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to study with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'resume',
        loadComponent: () =>
          import('./routes/about/resume/resume-content/resume-content.component').then(
            (m) => m.ResumeContentComponent,
          ),
        // #45: same reasoning as Bio — a bare "Resume" title gives a name
        // search nothing to click; baseline had clicks here, the 2026-08
        // window had none.
        title: 'Resume: Performance and Teaching History',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Resume', path: '/about/resume' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Professional resume and performance history of Russ Rodgers, bass guitarist and educator.',
          } satisfies PageSeoData,
          heading: {
            title: 'Resume',
            lead: "Russ Rodgers' teaching and performance experience at a glance.",
          } satisfies RouteHeading,
          related: [
            '/',
            '/about/bio',
            '/about/discography',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to study with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'gear',
        loadComponent: () =>
          import('./routes/about/gear/gear-content/gear-content.component').then(
            (m) => m.GearContentComponent,
          ),
        title: 'Gear',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Gear', path: '/about/gear' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              "Russ Rodgers's bass guitar gear — Ken Smith basses, Trickfish Audio amp, SFX Sound Micro-Thumpinator, Pedaltrain boards, Mogami Gold cables, and the rest of the rig.",
          } satisfies PageSeoData,
          heading: {
            title: 'Gear',
            lead: 'Instruments and equipment Russ plays and records with.',
          } satisfies RouteHeading,
          related: ['/', '/bass-lessons/zoom-setup'] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to talk gear and lessons with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: '**',
        redirectTo: '/about/bio',
      },
    ],
  },

  // Bass lessons routes — contact info inlined in each child
  {
    path: 'bass-lessons',
    children: [
      {
        path: 'play-along-tracks',
        loadComponent: () =>
          import('./routes/bass-lessons/play-along-tracks/play-along-tracks-content/play-along-tracks-content.component').then(
            (m) => m.PlayAlongTracksContentComponent,
          ),
        title: 'Play-Along Tracks',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            {
              name: 'Play-Along Tracks',
              path: '/bass-lessons/play-along-tracks',
            },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Free bass play-along tracks for working on feel, time, and subdivision. Easy blues, classic rock, R&B, reggae, and technique drills at multiple tempos.',
          } satisfies PageSeoData,
          heading: {
            title: 'Play-Along Tracks',
            lead: 'Free backing tracks for practicing feel, time, and subdivision at home. Multiple tempos per line.',
          } satisfies RouteHeading,
          related: [
            '/',
            '/bass-lessons/what-i-teach',
            '/bass-lessons/zoom-setup',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want lessons to go with these tracks?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'teaching-locations',
        redirectTo: '/',
      },
      {
        path: 'what-i-teach',
        loadComponent: () =>
          import('./routes/bass-lessons/what-i-teach/what-i-teach-content/what-i-teach-content.component').then(
            (m) => m.WhatITeachContentComponent,
          ),
        title: 'What I Teach',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            {
              name: 'What I Teach',
              path: '/bass-lessons/what-i-teach',
            },
          ] satisfies BreadcrumbItem[],
          // No static jsonLd here (deliberately): the four VideoObject +
          // ItemList schemas are set at runtime via SEOService, from inside
          // WhatITeachContentComponent's own lazy chunk — see
          // what-i-teach-jsonld.ts. Moving this out of the eager route data
          // is what keeps it out of every page's initial bundle (2026-09-14
          // perf pass).
          seo: {
            description:
              'How Russ Rodgers teaches bass guitar — styles (rock, pop, R&B, funk, jazz, gospel, Latin), note mapping with arpeggios and scales, ear development, reading, and the technique behind every style.',
          } satisfies PageSeoData,
          heading: {
            title: 'What I Teach',
            lead: 'Five foundations every student covers, shaped around your goals. Built from over forty years of teaching and over fifty years of professional playing.',
          } satisfies RouteHeading,
          related: [
            '/',
            '/bass-lessons/testimonials',
            '/bass-lessons/zoom-setup',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Want to start a curriculum like this?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'testimonials',
        loadComponent: () =>
          import('./routes/bass-lessons/testimonials/testimonials-content/testimonials-content.component').then(
            (m) => m.TestimonialsContentComponent,
          ),
        title: 'Testimonials',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            {
              name: 'Testimonials',
              path: '/bass-lessons/testimonials',
            },
          ] satisfies BreadcrumbItem[],
          // No static jsonLd here (deliberately): the VideoObject +
          // per-review Review schemas are set at runtime via SEOService,
          // from inside TestimonialsContentComponent's own lazy chunk — see
          // testimonials-jsonld.ts. Moving this out of the eager route data
          // is what keeps the ~28kB GOOGLE_REVIEWS array out of every page's
          // initial bundle (2026-09-14 perf pass).
          seo: {
            description:
              "Student testimonials and success stories from Russ Rodgers' bass guitar lessons.",
          } satisfies PageSeoData,
          heading: {
            title: 'What Students Say',
            lead: "Reviews and notes from students I've taught. Their words give you a better sense of the lessons than anything I could say about them myself.",
          } satisfies RouteHeading,
          related: [
            '/',
            '/bass-lessons/what-i-teach',
            '/bass-lessons/play-along-tracks',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Ready to join the students above?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'zoom-setup',
        loadComponent: () =>
          import('./routes/bass-lessons/zoom-setup/zoom-setup-content/zoom-setup-content.component').then(
            (m) => m.ZoomSetupContentComponent,
          ),
        title: 'Zoom Audio Setup for Online Music Lessons',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            {
              name: 'Zoom Setup for Online Bass Lessons',
              path: '/bass-lessons/zoom-setup',
            },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'The USB mixer, cables, and Zoom audio settings that make live music lessons sound right. Shopping list, hookup steps, and troubleshooting from a working online bass teacher.',
          } satisfies PageSeoData,
          heading: {
            title: 'Zoom Setup for Online Bass Lessons',
            lead: 'Equipment list, hookup guide, and software settings for the audio path that works for live online bass lessons.',
          } satisfies RouteHeading,
          related: [
            '/',
            '/bass-lessons/what-i-teach',
            '/bass-lessons/play-along-tracks',
          ] satisfies RelatedKey[],
          bottomCta: {
            heading: 'Ready to set up Zoom for lessons?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: '**',
        redirectTo: '/',
      },
    ],
  },

  // Blog — all children share the /blog prefix; literal child paths
  // (topic, tag, level) MUST precede the :slug catchall.
  {
    path: 'blog',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./routes/blog/blog-index/blog-index.component').then(
            (m) => m.BlogIndexComponent,
          ),
        title: 'Bass Guitar Lessons Blog',
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ] satisfies BreadcrumbItem[],
          // No static jsonLd here (deliberately): the Blog schema is set at
          // runtime via SEOService, from inside BlogIndexComponent's own
          // lazy chunk — see blog-index-jsonld.ts. Moving this out of the
          // eager route data is what keeps blog-jsonld.ts (previously
          // eagerly imported just for BLOG_AUTHOR_PERSON) out of every
          // page's initial bundle (2026-09-14 perf pass).
          seo: {
            description:
              'Bass guitar technique, theory, walking bass lines, and practice tips from instructor Russ Rodgers — over 40 years of teaching experience.',
          } satisfies PageSeoData,
          heading: {
            title: 'Bass Guitar Blog',
            lead: 'Bass guitar technique, theory, and practice tips from Russ Rodgers, bass guitar instructor since 1985.',
          } satisfies RouteHeading,
          // Blog renders its OWN bottom CTA (+ contextual related-posts on a
          // post), so it opts out of the shell's auto-appended Explore + CTA
          // pair to avoid double-rendering. See RouteUiDataService.
          suppressFooterCta: true,
          bottomCta: {
            heading: 'Want bass lessons with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'topic/:category',
        loadComponent: () =>
          import('./routes/blog/blog-topic/blog-topic.component').then(
            (m) => m.BlogTopicComponent,
          ),
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Articles on a specific bass guitar topic from instructor Russ Rodgers.',
          } satisfies PageSeoData,
          // Heading is set at runtime via RouteUiDataService.setHeadingOverride
          // inside the component effect — depends on the :category param.
          // Blog renders its OWN bottom CTA (+ contextual related-posts on a
          // post), so it opts out of the shell's auto-appended Explore + CTA
          // pair to avoid double-rendering. See RouteUiDataService.
          suppressFooterCta: true,
          bottomCta: {
            heading: 'Want bass lessons with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'tag/:tag',
        loadComponent: () =>
          import('./routes/blog/blog-tag/blog-tag.component').then(
            (m) => m.BlogTagComponent,
          ),
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Blog posts tagged for a specific bass guitar topic, by instructor Russ Rodgers.',
          } satisfies PageSeoData,
          // Heading is set at runtime via RouteUiDataService.setHeadingOverride
          // inside the component effect — depends on the :tag param.
          // Blog renders its OWN bottom CTA (+ contextual related-posts on a
          // post), so it opts out of the shell's auto-appended Explore + CTA
          // pair to avoid double-rendering. See RouteUiDataService.
          suppressFooterCta: true,
          bottomCta: {
            heading: 'Want bass lessons with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        path: 'difficulty/:level',
        loadComponent: () =>
          import('./routes/blog/blog-level/blog-level.component').then(
            (m) => m.BlogLevelComponent,
          ),
        data: {
          breadcrumbs: [
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ] satisfies BreadcrumbItem[],
          seo: {
            description:
              'Bass guitar blog posts filtered by difficulty level, from instructor Russ Rodgers.',
          } satisfies PageSeoData,
          // Heading is set at runtime via RouteUiDataService.setHeadingOverride
          // inside the component effect — depends on the :level param.
          // Blog renders its OWN bottom CTA (+ contextual related-posts on a
          // post), so it opts out of the shell's auto-appended Explore + CTA
          // pair to avoid double-rendering. See RouteUiDataService.
          suppressFooterCta: true,
          bottomCta: {
            heading: 'Want bass lessons with Russ?',
          } satisfies RouteBottomCta,
        },
      },
      {
        // :slug must come after the literal 'topic' | 'tag' | 'difficulty' paths.
        // Unknown slugs fall through to BlogPostComponent's "Post not found" state.
        path: ':slug',
        loadComponent: () =>
          import('./routes/blog/blog-post/blog-post.component').then(
            (m) => m.BlogPostComponent,
          ),
        // Title, description, and JSON-LD all set at runtime inside
        // BlogPostComponent via Title.setTitle + SEOService.setRouteSchemas.
        // Heading also set at runtime via RouteUiDataService.setHeadingOverride
        // because it depends on the :slug param.
        data: {
          // Caps the shell page-heading, breadcrumb trail and bottom-CTA pair
          // to the article's reading column (spec §14.2 item 1) so they share
          // its left AND right edges instead of the full shell-cap width.
          measure: 'article',
          // Blog renders its OWN bottom CTA + contextual related-posts, so it
          // opts out of the shell's auto-appended Explore + CTA pair.
          suppressFooterCta: true,
          bottomCta: {
            heading: 'Liked this post? Talk to Russ about lessons',
          } satisfies RouteBottomCta,
        },
      },
    ],
  },

  // X-Ploration Jazz — single page, no wrapper needed
  {
    path: 'x-ploration-jazz',
    loadComponent: () =>
      import('./routes/x-ploration-jazz/x-ploration-jazz-content/x-ploration-jazz-content.component').then(
        (m) => m.XPlorationJazzContentComponent,
      ),
    title: 'X-Ploration Jazz',
    data: {
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'X-Ploration Jazz', path: '/x-ploration-jazz' },
      ] satisfies BreadcrumbItem[],
      // No static jsonLd here (deliberately): the MusicGroup + VideoObject
      // schemas are set at runtime via SEOService, from inside
      // XPlorationJazzContentComponent's own lazy chunk — see
      // x-ploration-jazz-jsonld.ts. Moving this out of the eager route data
      // is what keeps it out of every page's initial bundle (2026-09-14
      // perf pass).
      seo: {
        description:
          'Live jazz for corporate events and private gatherings. X-Ploration Jazz is a three to five piece group led by Russ Rodgers, available around South Florida.',
      } satisfies PageSeoData,
      // Like home, this route extends the stage scene: the chrome flows straight
      // into the hero, which renders its own handoff wave into the paper body.
      stageExtension: true,
      // The hero already carries its own call/email conversion actions, so
      // the header CTA strip's "Questions about lessons?" prompt would be
      // redundant here. Same reasoning as home.
      suppressHeaderCta: true,
      // The component renders its own Explore tail + bottom reprise so they sit
      // inside the paper column; the shell's shared pair is not wanted here.
      suppressFooterCta: true,
      // The hero holds the single visible <h1> as real text (roadmap #72 — the
      // wordmark used to be pixels in Logo-2-jpeg.webp), so the shell
      // page-heading is suppressed with an empty title, exactly as home does.
      heading: { title: '' } satisfies RouteHeading,
      related: [
        '/about/performance',
        '/about/bio',
        '/about/credits',
      ] satisfies RelatedKey[],
    },
  },
  {
    path: '',
    // Lazy, like every other route here — home was the one eager
    // `component:` reference and its bundle (hub body, YouTube player,
    // pricing block, jump-nav, disclosure, schedule strip, testimonials
    // data) was dragged into every page's initial bundle as a result.
    // Measured: 594.17 kB -> 526.04 kB raw / 149.26 -> 133.57 kB transfer.
    loadComponent: () =>
      import('./routes/home/home-content/home-content.component').then(
        (m) => m.HomeContentComponent,
      ),
    data: {
      // No static jsonLd here (deliberately): the Course + FAQPage +
      // VideoObject schemas are set at runtime via SEOService, from
      // inside HomeContentComponent's own lazy chunk — see home-jsonld.ts.
      // Moving this out of the eager route data (previously evaluated at
      // bootstrap) is what keeps the ~7.3kB block off every page's
      // initial bundle (2026-09-14 perf pass).
      seo: {
        description:
          'Live one-on-one bass lessons over Zoom, built around the music you want to play. Teaching professionally since 1985; every lesson is recorded so you keep what we covered.',
      } satisfies PageSeoData,
      // Home extends the stage scene: the chrome flows straight into the
      // home-content hero (no shell chrome→paper handoff — the hero renders
      // its own handoff into the paper body). See RouteUiDataService.
      stageExtension: true,
      // Home's hero already carries its own call/email conversion actions,
      // so the header CTA strip's "Questions about lessons?" prompt would
      // be redundant here. See RouteUiDataService.
      suppressHeaderCta: true,
      // Home renders no Explore tiles (the hub body IS the wayfinding) and
      // its own bottom reprise; the shell's shared Explore + bottom-CTA pair
      // is not wanted here.
      suppressFooterCta: true,
      // The hero (home-content) holds the single visible <h1>, so the shell
      // page-heading is suppressed (empty title) — otherwise the page would
      // double-render a heading. Was a visually-hidden keyword h1; the hero
      // headline is now the on-page h1 (document <title> still carries the
      // "Online Bass Guitar Lessons" keyword via DEFAULT_TITLE).
      heading: { title: '' } satisfies RouteHeading,
      bottomCta: {
        heading: 'Your first lesson is one call away',
      } satisfies RouteBottomCta,
    },
  },
  {
    path: '**',
    redirectTo: '/',
  },
];
